import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { COUNTRY_CONFIGS, formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    dollar: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    wrench: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    zap: ['M13 2L3 14h9l-1 10 10-12h-9l1-10z'],
    activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
    check: ['M20 6L9 17l-5-5'],
    plus: 'M12 5v14 M5 12h14',
};

const STATUS_CONFIG = {
    'Active': { color: '#22c55e', bg: '#f0fdf4', label: 'Active' },
    'Inactive': { color: '#94a3b8', bg: '#f1f5f9', label: 'Inactive' },
    'Under Maintenance': { color: '#f59e0b', bg: '#fefce8', label: 'Maintenance' },
    'Damaged': { color: '#ef4444', bg: '#fef2f2', label: 'Damaged' },
    'Retired': { color: '#6b7280', bg: '#f3f4f6', label: 'Retired' },
    'In Transit': { color: '#3b82f6', bg: '#eff6ff', label: 'In Transit' },
};

export default function FleetDashboard({ user, onNavigate, operatorDetails }) {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchVehicles();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchVehicles = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/vehicles/${user?.operatorId || user?._id || 'all'}`);
            setVehicles(res.data);
        } catch (err) {
            console.error('Fetch vehicles error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Computed stats
    const stats = useMemo(() => {
        const total = vehicles.length;
        const active = vehicles.filter(v => v.vehicleStatus === 'Active').length;
        const maintenance = vehicles.filter(v => v.vehicleStatus === 'Under Maintenance').length;
        const damaged = vehicles.filter(v => v.vehicleStatus === 'Damaged').length;
        const inTransit = vehicles.filter(v => v.vehicleStatus === 'In Transit').length;
        const totalValue = vehicles.reduce((sum, v) => sum + (v.purchasePrice || 0), 0);
        const totalInsurance = vehicles.reduce((sum, v) => sum + (v.insuranceMonthly || 0), 0);
        const totalOther = vehicles.reduce((sum, v) => sum + (v.otherCostsMonthly || 0), 0);
        const years = vehicles.filter(v => v.year).map(v => new Date().getFullYear() - v.year);
        const avgAge = years.length > 0 ? (years.reduce((a, b) => a + b, 0) / years.length).toFixed(1) : 0;

        // Status breakdown
        const statusBreakdown = {};
        vehicles.forEach(v => {
            const s = v.vehicleStatus || 'Active';
            statusBreakdown[s] = (statusBreakdown[s] || 0) + 1;
        });

        // Fuel breakdown
        const fuelBreakdown = {};
        vehicles.forEach(v => {
            const f = v.fuelType || 'Unknown';
            fuelBreakdown[f] = (fuelBreakdown[f] || 0) + 1;
        });

        // Vehicle type breakdown
        const typeBreakdown = {};
        vehicles.forEach(v => {
            const t = v.vehicleType || 'Unknown';
            typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
        });

        // Recent vehicles (last 5)
        const recent = [...vehicles].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);

        return {
            total, active, maintenance, damaged, inTransit,
            totalValue, totalInsurance, totalOther, avgAge,
            statusBreakdown, fuelBreakdown, typeBreakdown, recent
        };
    }, [vehicles]);

    const FUEL_COLORS = ['#6366f1', '#f59e0b', '#22c55e', '#ef4444', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6'];

    // Simple horizontal bar for breakdowns
    const BreakdownBar = ({ data, colors }) => {
        const entries = Object.entries(data);
        const total = entries.reduce((s, [, v]) => s + v, 0);
        if (total === 0) return <div className="fd-empty-chart">No data yet</div>;
        return (
            <div className="fd-breakdown">
                <div className="fd-bar-track">
                    {entries.map(([key, val], i) => (
                        <div
                            key={key}
                            className="fd-bar-segment"
                            style={{
                                width: `${(val / total) * 100}%`,
                                background: colors ? colors[i % colors.length] : (STATUS_CONFIG[key]?.color || '#94a3b8'),
                            }}
                            title={`${key}: ${val}`}
                        />
                    ))}
                </div>
                <div className="fd-bar-legend">
                    {entries.map(([key, val], i) => (
                        <div key={key} className="fd-legend-item">
                            <span
                                className="fd-legend-dot"
                                style={{ background: colors ? colors[i % colors.length] : (STATUS_CONFIG[key]?.color || '#94a3b8') }}
                            />
                            <span className="fd-legend-label">{key}</span>
                            <span className="fd-legend-val">{val}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="fd-page">
            <style>{`
                .fd-page {
                    padding: 28px 30px; background: #f8fafc; min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }
                .fd-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
                .fd-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .fd-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                .fd-header-actions { display: flex; gap: 10px; }
                .fd-view-btn {
                    background: #1e1b4b; color: white; border: none; padding: 10px 20px;
                    border-radius: 10px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 13px; transition: all 0.2s; box-shadow: 0 4px 12px rgba(30,27,75,0.25);
                }
                .fd-view-btn:hover { background: #312e81; transform: translateY(-1px); }
                .fd-refresh-btn {
                    background: white; border: 1.5px solid #e2e8f0; padding: 10px 16px;
                    border-radius: 10px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;
                    font-size: 13px; color: #64748b; transition: all 0.2s;
                }
                .fd-refresh-btn:hover { border-color: #1e1b4b; color: #1e1b4b; }

                /* ── KPI Cards Row ── */
                .fd-kpi-row {
                    display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 24px;
                }
                .fd-kpi-card {
                    background: white; border-radius: 16px; padding: 22px 24px;
                    border: 1px solid #e2e8f0; position: relative; overflow: hidden;
                    transition: all 0.25s; box-shadow: 0 2px 8px rgba(0,0,0,0.02);
                }
                .fd-kpi-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
                .fd-kpi-card::before {
                    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
                }
                .fd-kpi-card:nth-child(1)::before { background: linear-gradient(90deg, #1e1b4b, #4338ca); }
                .fd-kpi-card:nth-child(2)::before { background: linear-gradient(90deg, #059669, #34d399); }
                .fd-kpi-card:nth-child(3)::before { background: linear-gradient(90deg, #d97706, #fbbf24); }
                .fd-kpi-card:nth-child(4)::before { background: linear-gradient(90deg, #dc2626, #f87171); }

                .fd-kpi-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
                .fd-kpi-label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
                .fd-kpi-icon {
                    width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                }
                .fd-kpi-value { font-size: 32px; font-weight: 800; color: #0f172a; line-height: 1; margin-bottom: 6px; }
                .fd-kpi-sub { font-size: 12px; color: #94a3b8; display: flex; align-items: center; gap: 4px; }
                .fd-kpi-sub .tag {
                    display: inline-flex; padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 600;
                }

                /* ── Grid Layout ── */
                .fd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
                .fd-card {
                    background: white; border-radius: 16px; padding: 24px;
                    border: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.02);
                }
                .fd-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .fd-card-title { font-size: 16px; font-weight: 700; color: #1e293b; }
                .fd-card-badge {
                    font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 99px;
                    background: #f0f9ff; color: #1e1b4b;
                }

                /* ── Breakdown Bars ── */
                .fd-breakdown { display: flex; flex-direction: column; gap: 14px; }
                .fd-bar-track {
                    display: flex; height: 12px; border-radius: 8px; overflow: hidden;
                    background: #f1f5f9; gap: 2px;
                }
                .fd-bar-segment {
                    border-radius: 6px; min-width: 8px; transition: width 0.5s ease;
                }
                .fd-bar-legend { display: flex; flex-wrap: wrap; gap: 12px; }
                .fd-legend-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #475569; }
                .fd-legend-dot { width: 8px; height: 8px; border-radius: 3px; flex-shrink: 0; }
                .fd-legend-label { font-weight: 500; }
                .fd-legend-val { font-weight: 700; color: #1e293b; }
                .fd-empty-chart {
                    display: flex; align-items: center; justify-content: center;
                    height: 80px; color: #94a3b8; font-size: 13px; font-style: italic;
                }

                /* ── Status Grid ── */
                .fd-status-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
                .fd-status-item {
                    padding: 14px; border-radius: 12px; text-align: center;
                    display: flex; flex-direction: column; align-items: center; gap: 4px;
                    transition: all 0.2s;
                }
                .fd-status-item:hover { transform: scale(1.03); }
                .fd-status-count { font-size: 22px; font-weight: 800; }
                .fd-status-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; }

                /* ── Recent Vehicles ── */
                .fd-recent-list { display: flex; flex-direction: column; gap: 0; }
                .fd-recent-item {
                    display: flex; align-items: center; gap: 14px; padding: 14px 0;
                    border-bottom: 1px solid #f1f5f9; transition: background 0.15s;
                }
                .fd-recent-item:last-child { border-bottom: none; }
                .fd-recent-color {
                    width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
                    border: 2px solid rgba(0,0,0,0.06);
                }
                .fd-recent-info { flex: 1; min-width: 0; }
                .fd-recent-plate { font-weight: 700; color: #1e293b; font-size: 14px; }
                .fd-recent-make { font-size: 12px; color: #64748b; }
                .fd-recent-status {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 600;
                }

                /* ── Cost Summary ── */
                .fd-cost-row { display: flex; flex-direction: column; gap: 12px; }
                .fd-cost-item {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 14px 18px; border-radius: 12px; background: #f8fafc;
                    border: 1px solid #f1f5f9;
                }
                .fd-cost-left { display: flex; align-items: center; gap: 12px; }
                .fd-cost-icon {
                    width: 36px; height: 36px; border-radius: 10px; display: flex;
                    align-items: center; justify-content: center;
                }
                .fd-cost-label { font-size: 13px; font-weight: 600; color: #475569; }
                .fd-cost-val { font-size: 16px; font-weight: 800; color: #0f172a; }
                .fd-cost-period { font-size: 11px; color: #94a3b8; }

                /* ── Full Width Card ── */
                .fd-full-card {
                    background: white; border-radius: 16px; padding: 24px;
                    border: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.02);
                }

                /* ── Quick Actions ── */
                .fd-actions-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 4px; }
                .fd-action-card {
                    padding: 18px; border-radius: 14px; cursor: pointer;
                    display: flex; flex-direction: column; align-items: center; gap: 10px;
                    text-align: center; transition: all 0.25s; border: 1.5px solid #e2e8f0;
                    background: white;
                }
                .fd-action-card:hover { border-color: #1e1b4b; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(30,27,75,0.1); }
                .fd-action-icon {
                    width: 44px; height: 44px; border-radius: 12px; display: flex;
                    align-items: center; justify-content: center;
                }
                .fd-action-label { font-size: 13px; font-weight: 600; color: #334155; }
                .fd-action-desc { font-size: 11px; color: #94a3b8; }

                @media (max-width: 1024px) {
                    .fd-kpi-row { grid-template-columns: repeat(2, 1fr); }
                    .fd-grid { grid-template-columns: 1fr; }
                    .fd-actions-row { grid-template-columns: repeat(2, 1fr); }
                }
            `}</style>

            {/* Header */}
            <div className="fd-header">
                <div>
                    <h1 className="fd-title">Fleet Dashboard</h1>
                    <p className="fd-subtitle">Real-time overview of your fleet operations, health, and costs</p>
                </div>
                <div className="fd-header-actions">
                    <button className="fd-refresh-btn" onClick={fetchVehicles}>
                        <Icon d={icons.activity} size={15} /> Refresh
                    </button>
                    <button className="fd-view-btn" onClick={() => onNavigate && onNavigate('all-vehicles')}>
                        <Icon d={icons.truck} size={16} /> View All Vehicles
                    </button>
                </div>
            </div>

            {/* KPI Row */}
            <div className="fd-kpi-row">
                <div className="fd-kpi-card">
                    <div className="fd-kpi-top">
                        <span className="fd-kpi-label">Total Fleet</span>
                        <div className="fd-kpi-icon" style={{ background: '#eef2ff' }}>
                            <Icon d={icons.truck} size={20} color="#4338ca" />
                        </div>
                    </div>
                    <div className="fd-kpi-value">{stats.total}</div>
                    <div className="fd-kpi-sub">
                        <span className="tag" style={{ background: '#f0fdf4', color: '#166534' }}>{stats.active} active</span>
                        vehicles registered
                    </div>
                </div>

                <div className="fd-kpi-card">
                    <div className="fd-kpi-top">
                        <span className="fd-kpi-label">Total Fleet Value</span>
                        <div className="fd-kpi-icon" style={{ background: '#ecfdf5' }}>
                            <Icon d={icons.dollar} size={20} color="#059669" />
                        </div>
                    </div>
                    <div className="fd-kpi-value" style={{ fontSize: 26 }}>
                        {stats.totalValue.toFixed(3)} <span style={{ fontSize: 16, color: '#64748b', fontWeight: 600 }}>{COUNTRY_CONFIGS[operatorDetails?.country]?.currency || 'KWD'}</span>
                    </div>
                    <div className="fd-kpi-sub">Total purchase value</div>
                </div>

                <div className="fd-kpi-card">
                    <div className="fd-kpi-top">
                        <span className="fd-kpi-label">Under Maintenance</span>
                        <div className="fd-kpi-icon" style={{ background: '#fefce8' }}>
                            <Icon d={icons.wrench} size={20} color="#d97706" />
                        </div>
                    </div>
                    <div className="fd-kpi-value">{stats.maintenance}</div>
                    <div className="fd-kpi-sub">
                        {stats.damaged > 0 && <span className="tag" style={{ background: '#fef2f2', color: '#dc2626' }}>{stats.damaged} damaged</span>}
                        vehicles in service
                    </div>
                </div>

                <div className="fd-kpi-card">
                    <div className="fd-kpi-top">
                        <span className="fd-kpi-label">Monthly Costs</span>
                        <div className="fd-kpi-icon" style={{ background: '#fef2f2' }}>
                            <Icon d={icons.zap} size={20} color="#dc2626" />
                        </div>
                    </div>
                    <div className="fd-kpi-value" style={{ fontSize: 26 }}>
                        {(stats.totalInsurance + stats.totalOther).toFixed(3)} <span style={{ fontSize: 16, color: '#64748b', fontWeight: 600 }}>{COUNTRY_CONFIGS[operatorDetails?.country]?.currency || 'KWD'}</span>
                    </div>
                    <div className="fd-kpi-sub">Insurance + Other costs</div>
                </div>
            </div>

            {/* Middle Row: Status + Fuel Type */}
            <div className="fd-grid">
                {/* Vehicle Status Breakdown */}
                <div className="fd-card">
                    <div className="fd-card-header">
                        <span className="fd-card-title">Vehicle Status Overview</span>
                        <span className="fd-card-badge">{stats.total} Total</span>
                    </div>
                    <div className="fd-status-grid">
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                            <div
                                key={key}
                                className="fd-status-item"
                                style={{ background: cfg.bg }}
                            >
                                <div className="fd-status-count" style={{ color: cfg.color }}>
                                    {stats.statusBreakdown[key] || 0}
                                </div>
                                <div className="fd-status-label" style={{ color: cfg.color }}>
                                    {cfg.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Fuel Type Distribution */}
                <div className="fd-card">
                    <div className="fd-card-header">
                        <span className="fd-card-title">Fuel Type Distribution</span>
                        <span className="fd-card-badge">Breakdown</span>
                    </div>
                    {Object.keys(stats.fuelBreakdown).length === 0 ? (
                        <div className="fd-empty-chart">No vehicle data available</div>
                    ) : (
                        <BreakdownBar data={stats.fuelBreakdown} colors={FUEL_COLORS} />
                    )}

                    {Object.keys(stats.typeBreakdown).length > 0 && (
                        <>
                            <div style={{ margin: '20px 0 12px', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Vehicle Types</div>
                            <BreakdownBar data={stats.typeBreakdown} colors={['#1e1b4b', '#4338ca', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe']} />
                        </>
                    )}
                </div>
            </div>

            {/* Bottom Row: Recent + Cost Summary */}
            <div className="fd-grid">
                {/* Recent Vehicles */}
                <div className="fd-card">
                    <div className="fd-card-header">
                        <span className="fd-card-title">Recently Added</span>
                        <span className="fd-card-badge">Last 5</span>
                    </div>
                    {stats.recent.length === 0 ? (
                        <div className="fd-empty-chart">No vehicles added yet</div>
                    ) : (
                        <div className="fd-recent-list">
                            {stats.recent.map(v => {
                                const sCfg = STATUS_CONFIG[v.vehicleStatus] || STATUS_CONFIG['Active'];
                                return (
                                    <div key={v._id} className="fd-recent-item">
                                        <div className="fd-recent-color" style={{ background: v.color || '#e2e8f0' }} />
                                        <div className="fd-recent-info">
                                            <div className="fd-recent-plate">{v.plateNumber}</div>
                                            <div className="fd-recent-make">{v.make} {v.model} {v.year ? `· ${v.year}` : ''}</div>
                                        </div>
                                        <span className="fd-recent-status" style={{ background: sCfg.bg, color: sCfg.color }}>{v.vehicleStatus}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Cost Summary */}
                <div className="fd-card">
                    <div className="fd-card-header">
                        <span className="fd-card-title">Monthly Cost Summary</span>
                        <span className="fd-card-badge">Per Month</span>
                    </div>
                    <div className="fd-cost-row">
                        <div className="fd-cost-item">
                            <div className="fd-cost-left">
                                <div className="fd-cost-icon" style={{ background: '#eef2ff' }}>
                                    <Icon d={icons.shield} size={18} color="#4338ca" />
                                </div>
                                <span className="fd-cost-label">Insurance</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div className="fd-cost-val">{stats.totalInsurance.toFixed(3)} {COUNTRY_CONFIGS[operatorDetails?.country]?.currency || 'KWD'}</div>
                                <div className="fd-cost-period">per month</div>
                            </div>
                        </div>
                        <div className="fd-cost-item">
                            <div className="fd-cost-left">
                                <div className="fd-cost-icon" style={{ background: '#fefce8' }}>
                                    <Icon d={icons.wrench} size={18} color="#d97706" />
                                </div>
                                <span className="fd-cost-label">Other Costs</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div className="fd-cost-val">{stats.totalOther.toFixed(3)} {COUNTRY_CONFIGS[operatorDetails?.country]?.currency || 'KWD'}</div>
                                <div className="fd-cost-period">per month</div>
                            </div>
                        </div>
                        <div className="fd-cost-item" style={{ background: '#1e1b4b', border: 'none' }}>
                            <div className="fd-cost-left">
                                <div className="fd-cost-icon" style={{ background: 'rgba(255,255,255,0.15)' }}>
                                    <Icon d={icons.dollar} size={18} color="#fff" />
                                </div>
                                <span className="fd-cost-label" style={{ color: 'rgba(255,255,255,0.7)' }}>Total Monthly</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div className="fd-cost-val" style={{ color: '#fff' }}>{(stats.totalInsurance + stats.totalOther).toFixed(3)} {COUNTRY_CONFIGS[operatorDetails?.country]?.currency || 'KWD'}</div>
                                <div className="fd-cost-period" style={{ color: 'rgba(255,255,255,0.5)' }}>per month</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="fd-full-card">
                <div className="fd-card-header">
                    <span className="fd-card-title">Quick Actions</span>
                </div>
                <div className="fd-actions-row">
                    <div className="fd-action-card" onClick={() => onNavigate && onNavigate('all-vehicles')}>
                        <div className="fd-action-icon" style={{ background: '#eef2ff' }}>
                            <Icon d={icons.truck} size={22} color="#4338ca" />
                        </div>
                        <span className="fd-action-label">Manage Vehicles</span>
                        <span className="fd-action-desc">Add, edit or remove fleet vehicles</span>
                    </div>
                    <div className="fd-action-card" onClick={() => onNavigate && onNavigate('drivers')}>
                        <div className="fd-action-icon" style={{ background: '#ecfdf5' }}>
                            <Icon d={icons.check} size={22} color="#059669" />
                        </div>
                        <span className="fd-action-label">Driver Management</span>
                        <span className="fd-action-desc">View and assign drivers</span>
                    </div>
                    <div className="fd-action-card" onClick={() => onNavigate && onNavigate('vendor-fleet')}>
                        <div className="fd-action-icon" style={{ background: '#fefce8' }}>
                            <Icon d={icons.wrench} size={22} color="#d97706" />
                        </div>
                        <span className="fd-action-label">Vendor Fleet</span>
                        <span className="fd-action-desc">Manage vendor vehicle assignments</span>
                    </div>
                    <div className="fd-action-card" onClick={() => onNavigate && onNavigate('vendor-pricing')}>
                        <div className="fd-action-icon" style={{ background: '#fef2f2' }}>
                            <Icon d={icons.dollar} size={22} color="#dc2626" />
                        </div>
                        <span className="fd-action-label">Pricing & Costs</span>
                        <span className="fd-action-desc">Review fleet financial data</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
